<template>
    <!-- Inline A2A Agent Card for a single agent (PLT-1615). Mounted lazily when the agent
         row is expanded, so it fetches its own card on mount. Shows what the agent
         advertises — notably capabilities.streaming. -->
    <div class="pb-3 pl-1">
        <div v-if="error" class="rounded-md border border-red-300 bg-red-50 px-4 py-2 body-small text-red-800 whitespace-pre-wrap break-words">
            {{ error }}
        </div>

        <div v-else-if="loading" class="body-small text-valence-grey-500">Loading card…</div>

        <div v-else-if="card" class="flex flex-col gap-2">
            <div class="flex items-center flex-wrap gap-2">
                <span v-if="card.version" class="text-xs text-valence-grey-500">v{{ card.version }}</span>
                <span
                    class="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded"
                    :class="card.capabilities?.streaming ? 'bg-green-100 text-green-700' : 'bg-valence-grey-100 text-valence-grey-600'">
                    {{ card.capabilities?.streaming ? "Streaming ✓" : "No streaming" }}
                </span>
                <span
                    v-if="card.capabilities?.pushNotifications"
                    class="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-valence-grey-100 text-valence-grey-600">
                    Push ✓
                </span>
            </div>
            <p v-if="card.description" class="body-small text-valence-grey-750">{{ card.description }}</p>
            <div v-if="skillNames" class="body-small text-valence-grey-750">Skills: {{ skillNames }}</div>
            <button type="button" class="self-start text-xs text-valence-blue" @click="showRaw = !showRaw">{{ showRaw ? "Hide" : "Show" }} raw card</button>
            <pre v-if="showRaw" class="text-xs bg-valence-grey-50 border border-[#E8E8E8] rounded-lg p-3 overflow-auto max-h-72">{{ pretty(card) }}</pre>
        </div>

        <div v-else class="body-small text-valence-grey-500">No card available for this agent.</div>
    </div>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import { computed, ref } from "vue";

interface AgentCardSkill {
    id?: string;
    name?: string;
    description?: string;
}
interface AgentCard {
    name?: string;
    description?: string;
    version?: string;
    capabilities?: { streaming?: boolean; pushNotifications?: boolean };
    skills?: AgentCardSkill[];
    provider?: { organization?: string; url?: string };
}

const props = defineProps<{
    agentId: string;
    // Base agent-card endpoint; the agent id is appended as ?agent_id= (URL-encoded).
    cardUrl: string;
}>();

const card = ref<AgentCard | null>(null);
const error = ref("");
const loading = ref(false);
const showRaw = ref(false);

const skillNames = computed(() =>
    (card.value?.skills ?? [])
        .map((skill) => skill.name || skill.id)
        .filter(Boolean)
        .join(", "),
);

function pretty(value: unknown): string {
    return JSON.stringify(value, null, 2);
}

async function loadCard() {
    loading.value = true;
    error.value = "";
    try {
        const { data, response } = await useFetch(`${props.cardUrl}?agent_id=${encodeURIComponent(props.agentId)}`)
            .get()
            .json();
        if (response.value?.ok) {
            card.value = data.value?.card ?? null;
        } else {
            card.value = null;
            error.value = data.value?.error || "Failed to load agent card.";
        }
    } finally {
        loading.value = false;
    }
}

loadCard();
</script>
